import React, { useMemo } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd/es/table/InternalTable';
import styled from 'styled-components';

import DownCaretIcon from 'common/Icons/DownCaretIcon';
import Dropdown from 'components/lib/Dropdown';
import { SimpleListActionButtonContent, SimpleListTableEmptyState } from 'components/lib/SimpleListModalComponents';
import { SimpleListTable } from 'components/lib/SimpleListTable';

import {
  ActionsCell,
  CompactActionButton,
  compactContactTableStyles,
  fillContactTableStyles,
  MutedText,
  renderContactIdentity,
  renderMailboxStatus,
  ValueText,
} from './ShopperContactsTableShared';
import { ShopRequestShopperEmailType } from '../../types';

const BaseTable = (props: TableProps<ShopRequestShopperEmailType>) => {
  return <Table<ShopRequestShopperEmailType> {...props} />;
};

const StyledTable = styled(BaseTable)`
  ${SimpleListTable}
  ${compactContactTableStyles}
  ${fillContactTableStyles}

  &&&&&& {
    width: 100%;

    .actions-hover-reveal {
      visibility: hidden;
    }

    .ant-table-cell-row-hover {
      .actions-hover-reveal {
        visibility: visible;
      }
    }
  }
`;

type Props = {
  shopperEmails: ShopRequestShopperEmailType[];
  isFetchingShopperEmails: boolean;
  isLoadingShopperEmails: boolean;
  isDeleting: boolean;
  authenticatingEmailId: number | null;
  syncingEmailId: number | null;
  onDelete: (shopperEmail: ShopRequestShopperEmailType) => void;
  onEdit: (shopperEmail: ShopRequestShopperEmailType) => void;
  onMailboxAction: (shopperEmail: ShopRequestShopperEmailType) => void | Promise<void>;
  onSyncMailbox: (shopperEmailId?: number) => void | Promise<void>;
};

const ManageShopRequestShopperEmailsTable = ({
  shopperEmails,
  isFetchingShopperEmails,
  isLoadingShopperEmails,
  isDeleting,
  authenticatingEmailId,
  syncingEmailId,
  onDelete,
  onEdit,
  onMailboxAction,
  onSyncMailbox,
}: Props) => {
  const columns = useMemo<ColumnsType<ShopRequestShopperEmailType>>(
    () => [
      {
        title: 'Contact',
        dataIndex: 'name',
        key: 'contact',
        render: (_value: string | undefined, record) => renderContactIdentity(record),
      },
      {
        title: 'Label',
        dataIndex: 'label',
        key: 'label',
        render: (value?: string | null) => (value ? <ValueText>{value}</ValueText> : <MutedText>No label</MutedText>),
      },
      {
        title: 'Mailbox Status',
        dataIndex: 'mailbox_connection_status',
        key: 'mailbox_connection_status',
        width: 160,
        render: (value: string | null | undefined, record) =>
          renderMailboxStatus(value, record.email, syncingEmailId === record.id),
      },
      {
        title: '',
        key: 'actions',
        width: 100,
        render: (_value, record) => {
          const isAuthenticating = authenticatingEmailId === record.id;
          const isSyncing = syncingEmailId === record.id;
          const isBusy = isDeleting || isAuthenticating || isSyncing;
          const menuItems = [
            {
              label: 'Edit',
              key: 'edit',
              disabled: isBusy,
              onClick: () => onEdit(record),
            },
            ...(record.email
              ? record.mailbox_connection_id
                ? [
                    {
                      label: 'Sync',
                      key: 'sync-mailbox',
                      disabled: isBusy || !record.id,
                      onClick: () => {
                        onSyncMailbox(record.id);
                      },
                    },
                  ]
                : [
                    {
                      label: 'Link Mailbox',
                      key: 'link-mailbox',
                      disabled: isBusy || !record.id,
                      onClick: () => {
                        onMailboxAction(record);
                      },
                    },
                  ]
              : []),
            {
              label: 'Remove',
              key: 'unlink',
              disabled: isBusy,
              onClick: () => onDelete(record),
            },
          ];

          return (
            <ActionsCell className="actions-hover-reveal">
              <Dropdown placement="bottomRight" menu={{ items: menuItems }}>
                <CompactActionButton disabled={isBusy}>
                  <SimpleListActionButtonContent>
                    <span>{isBusy ? 'Loading' : 'Edit'}</span>
                    {isBusy ? <LoadingOutlined /> : <DownCaretIcon />}
                  </SimpleListActionButtonContent>
                </CompactActionButton>
              </Dropdown>
            </ActionsCell>
          );
        },
      },
    ],
    [authenticatingEmailId, isDeleting, onDelete, onEdit, onMailboxAction, onSyncMailbox, syncingEmailId]
  );

  return (
    <StyledTable
      rowKey={(record) => record.id ?? record.shopper_contact_id ?? record.email ?? record.phone ?? 'shopper-contact'}
      columns={columns}
      dataSource={shopperEmails}
      pagination={false}
      scroll={{ y: '100%' }}
      loading={isLoadingShopperEmails || isFetchingShopperEmails}
      locale={{
        emptyText: <SimpleListTableEmptyState>No linked shopper contacts found.</SimpleListTableEmptyState>,
      }}
    />
  );
};

export default ManageShopRequestShopperEmailsTable;
